'use client';

import Icon from '@/components/ui/AppIcon';

interface RelatedPart {
  name: string;
  reason: string;
  icon: string;
}

interface RelatedPartsProps {
  vehicleYear: string;
  vehicleMileage: string;
  onPartClick: (partName: string) => void;
}

const RelatedParts = ({ vehicleYear, vehicleMileage, onPartClick }: RelatedPartsProps) => {
  const relatedParts: RelatedPart[] = [
    {
      name: 'Cabin Air Filter',
      reason: 'Recommended every 15,000 miles',
      icon: 'FunnelIcon'
    },
    {
      name: 'Engine Air Filter',
      reason: 'Essential for engine performance',
      icon: 'CpuChipIcon'
    },
    {
      name: 'Wiper Blades',
      reason: 'Replace annually for safety',
      icon: 'CloudIcon'
    },
    {
      name: 'Brake Fluid',
      reason: 'Critical for brake system',
      icon: 'BeakerIcon'
    }
  ];

  const mileage = parseInt(vehicleMileage) || 0;
  const year = parseInt(vehicleYear) || new Date().getFullYear();
  const vehicleAge = new Date().getFullYear() - year;

  const getMaintenanceRecommendations = () => {
    const recommendations: RelatedPart[] = [];

    if (mileage > 60000) {
      recommendations.push({
        name: 'Spark Plugs',
        reason: `Recommended at ${mileage.toLocaleString()} miles`,
        icon: 'BoltIcon'
      });
    }

    if (mileage > 100000) {
      recommendations.push({
        name: 'Timing Belt',
        reason: 'Critical replacement at high mileage',
        icon: 'Cog6ToothIcon'
      });
    }

    if (vehicleAge > 5) {
      recommendations.push({
        name: 'Battery',
        reason: `Vehicle is ${vehicleAge} years old`,
        icon: 'BoltIcon'
      });
    }

    return recommendations;
  };

  const maintenanceRecommendations = getMaintenanceRecommendations();
  const allParts = [...maintenanceRecommendations, ...relatedParts];

  if (allParts.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6 space-y-4">
      <div className="flex items-center space-x-2">
        <Icon name="LightBulbIcon" size={24} className="text-accent" />
        <h3 className="font-heading text-lg font-semibold text-text-primary">
          Related Parts & Maintenance
        </h3>
      </div>

      {maintenanceRecommendations.length > 0 && (
        <div className="space-y-2">
          <p className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
            Based on Your Vehicle
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {maintenanceRecommendations.map((part, index) => (
              <button
                key={index}
                onClick={() => onPartClick(part.name)}
                className="flex items-start space-x-3 rounded-lg border border-accent/30 bg-accent/5 p-4 text-left transition-smooth hover:border-accent hover:bg-accent/10 active:scale-[0.98]"
              >
                <Icon name={part.icon as any} size={20} className="text-accent flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                    {part.name}
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    {part.reason}
                  </p>
                </div>
                <Icon name="ChevronRightIcon" size={16} className="text-accent flex-shrink-0 mt-1" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
          Common Maintenance Items
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {relatedParts.map((part, index) => (
            <button
              key={index}
              onClick={() => onPartClick(part.name)}
              className="flex items-start space-x-3 rounded-lg border border-border bg-muted/50 p-4 text-left transition-smooth hover:border-primary hover:bg-muted active:scale-[0.98]"
            >
              <Icon name={part.icon as any} size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-heading text-sm font-semibold text-text-primary mb-1">
                  {part.name}
                </p>
                <p className="font-body text-xs text-text-secondary">
                  {part.reason}
                </p>
              </div>
              <Icon name="ChevronRightIcon" size={16} className="text-text-secondary flex-shrink-0 mt-1" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RelatedParts;